import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, StatusBadge, Spinner, money, Button } from "@/components/ui";
import { toast } from "sonner";
import { Plus, Receipt } from "lucide-react";

function Stat({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/invoices")])
      .then(([s, inv]) => {
        setStats(s.data);
        setInvoices(inv.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex justify-center py-20 text-primary"><Spinner /></div>
    );

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Invoices" value={stats?.invoices ?? 0} />
        <Stat label="Collected" value={money(stats?.collected_cents, "usd")} />
        <Stat label="Monthly recurring" value={money(stats?.monthly_recurring_cents, "usd")} />
        <Stat label="Awaiting payment" value={stats?.pending ?? 0} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Invoices</h2>
          <Link to="/invoices/new"><Button size="sm"><Plus className="h-4 w-4" /> New invoice</Button></Link>
        </div>

        {invoices.length === 0 ? (
          <Card className="flex flex-col items-center justify-center py-16 text-center">
            <Receipt className="h-8 w-8 text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No invoices yet.</p>
            <Link to="/invoices/new" className="mt-4"><Button><Plus className="h-4 w-4" /> Create your first invoice</Button></Link>
          </Card>
        ) : (
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">One-time</th>
                  <th className="px-4 py-3 font-medium">Monthly</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-border/50 hover:bg-secondary/40">
                    <td className="px-4 py-3">
                      <div className="font-medium">{inv.customer_name}</div>
                      <div className="text-xs text-muted-foreground">{inv.customer_email}</div>
                    </td>
                    <td className="px-4 py-3 mono">{money(inv.one_time_total_cents, inv.currency)}</td>
                    <td className="px-4 py-3 mono">
                      {inv.monthly_total_cents ? `${money(inv.monthly_total_cents, inv.currency)}/mo` : "—"}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/invoices/${inv.id}`} className="text-primary hover:underline">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
